import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ConfirmDialog } from '@saas-kit/ui';

import type { AdminTenant } from './api';

type ActionKey = 'suspend' | 'reactivate' | 'archive';

interface Props {
  tenant: AdminTenant;
  pendingAction: ActionKey | null;
  onSuspend: (tenant: AdminTenant) => void;
  onReactivate: (tenant: AdminTenant) => void;
  onArchive: (tenant: AdminTenant) => void;
}

export function TenantActions({
  tenant,
  pendingAction,
  onSuspend,
  onReactivate,
  onArchive,
}: Props): JSX.Element | null {
  const { t } = useTranslation('adminTenants');
  const [confirm, setConfirm] = useState<ActionKey | null>(null);

  if (tenant.status === 'archived') return null;

  const isPending = pendingAction !== null;

  function handleConfirm(): void {
    if (!confirm) return;
    if (confirm === 'suspend') onSuspend(tenant);
    if (confirm === 'reactivate') onReactivate(tenant);
    if (confirm === 'archive') onArchive(tenant);
    setConfirm(null);
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {tenant.status === 'active' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirm('suspend')}
            disabled={isPending}
          >
            {t('actions.suspend')}
          </Button>
        ) : null}
        {tenant.status === 'suspended' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirm('reactivate')}
            disabled={isPending}
          >
            {t('actions.reactivate')}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirm('archive')}
          disabled={isPending}
        >
          {t('actions.archive')}
        </Button>
      </div>

      {confirm ? (
        <ConfirmDialog
          title={t(`confirm.${confirm}.title`, { name: tenant.name })}
          body={t(`confirm.${confirm}.body`, { name: tenant.name })}
          confirmLabel={t(`actions.${confirm}`)}
          variant={confirm === 'archive' ? 'destructive' : 'primary'}
          isBusy={pendingAction === confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirm}
        />
      ) : null}
    </>
  );
}
